import { ItemBarLeft } from "components/ItemBar";
import { events } from "constants/tagManager";
import { ToastContext } from "context/ToastContext";
import { useAnalytics } from "hooks/useAnalytics";
import { mapFormToRequestBody } from "pages/CreateOrder/utils/helpers";
import { Button } from "primereact/button";
import { useContext, useMemo, useState } from "react";
import { useFormContext, useFormState } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState } from "store";
import { useSaveDraftMutation } from "store/queries/order";
import { cleanUpServiceDetails } from "store/slices/serviceDetails";

export function SaveDraft() {
  const dispatch = useDispatch();
  const [saveDraft] = useSaveDraftMutation();
  const { getValues, watch, trigger } = useFormContext();
  const { pageViewEvents } = useAnalytics();
  const [loading, setLoading] = useState(false);
  const { isValid } = useFormState();
  const navigate = useNavigate();
  const procedures = watch("serviceDetails");
  const { selectedDraftOrder, integrationErrors } = useSelector(
    (state: RootState) => state.orderSlice
  );
  const { loadingPriceBreakdown } = useSelector(
    (state: RootState) => state.serviceDetailsSlice
  );
  const { current: toastElement } = useContext(ToastContext);

  const integrationErrorsParsed = useMemo(() => {
    return integrationErrors
      .filter((error) => error.active)
      .reduce((acc, error) => {
        acc[error.errorType] = error.message;
        return acc;
      }, {} as { [key: string]: string });
  }, [integrationErrors]);

  const isButtonDisabled = useMemo(() => {
    const hasEmptyCptDescription = getValues()?.serviceDetails?.some(
      (procedure: any) => !procedure.cptDescription.trim().length
    );
    return !isValid || hasEmptyCptDescription;
  }, [isValid, procedures]);

  const updateDraftOrder = async () => {
    const isValid = await trigger("scheduledFirstCommunicationDate");
    if (!isValid) {
      return;
    }
    const requestBody = mapFormToRequestBody(getValues(), true);
    setLoading(true);
    saveDraft({
      ...selectedDraftOrder,
      ...requestBody,
      status: "Draft",
      patient: { ...selectedDraftOrder.patient, ...requestBody.patient },
      integrationErrors: integrationErrorsParsed,
      updatedAt: new Date().toISOString(),
      benefitDetails: requestBody.benefitDetails,
      secondaryBenefitDetails: requestBody.secondaryBenefitDetails,
    })
      .then(() => {
        pageViewEvents(
          {
            accountName: selectedDraftOrder.account.name,
            orderId: selectedDraftOrder.id,
            status: "Draft",
          },
          events.ORDER_UPDATED
        );

        setLoading(false);
        dispatch(cleanUpServiceDetails());
        toastElement?.show({
          severity: "success",
          summary: "Pending Review Estimate changed",
          detail:
            "Pending Review Estimate changed successfully but not approved",
        });
        navigate(`/hospitals/view/${selectedDraftOrder.account.id}/orders`);
      })
      .catch((error) => {
        toastElement?.show({
          severity: "error",
          summary: "Error",
          detail: `Message: ${error}`,
        });
      });
  };

  return (
    <ItemBarLeft>
      <div data-testid="saveDraft_ReviewOrder" className="pb-3">
        <Button
          label="Save Draft"
          onClick={() => updateDraftOrder()}
          loading={loadingPriceBreakdown || loading}
          disabled={isButtonDisabled}
        />
      </div>
    </ItemBarLeft>
  );
}
